﻿/**
 * Assessment Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import { useAssessmentStore } from '../../store/assessment.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class AssessmentIntegration {
    constructor(sdkService, questionService) {
        this.service = sdkService;
        this.questionService = questionService;
    }

    /**
     * Fetch assessments and update store
     */
    async fetchAssessments(overrides = {}) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        const page = overrides.page || store.pagination?.currentPage || 1;
        const pageSize = overrides.pageSize || store.pagination?.itemsPerPage || 10;
        const search = overrides.search !== undefined ? overrides.search : (store.searchQuery || '');
        const sortBy = overrides.sortBy !== undefined ? overrides.sortBy : store.sortBy;
        const sortOrder = overrides.sortDirection !== undefined ? overrides.sortDirection : store.sortDirection;
        const columnFilters = overrides.columnFilters || store.columnFilters || {};

        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        if (!tenantId) {
            if (store.setLoading) store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;

            if (store.setAssessments) {
                store.setAssessments(data || []);
            }

            if (pagination && store.setPagination) {
                store.setPagination({
                    currentPage: pagination.page,
                    itemsPerPage: pagination.pageSize,
                    totalItems: pagination.total,
                    totalPages: pagination.totalPages,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1
                });
            }

            if (uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create assessment
     */
    async createAssessment(data) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (store.setLoading) store.setLoading(true);

        if (!tenantId) {
            if (store.setLoading) store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.create({ ...data, tenantId });
            toast.success(i18next.t('internalAuditAssessment:messages.createSuccess', { defaultValue: 'Evaluation created successfully' }));
            this.fetchAssessments();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Fetch single assessment by ID (full detail)
     */
    async fetchAssessmentById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Update assessment
     */
    async updateAssessment(id, data) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (store.setLoading) store.setLoading(true);

        if (!tenantId) {
            if (store.setLoading) store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('internalAuditAssessment:messages.updateSuccess', { defaultValue: 'Evaluation updated successfully' }));
            this.fetchAssessments();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete assessment
     */
    async deleteAssessment(id) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (store.setLoading) store.setLoading(true);

        if (!tenantId) {
            if (store.setLoading) store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('internalAuditAssessment:messages.deleteSuccess', { defaultValue: 'Evaluation deleted successfully' }));
            this.fetchAssessments();
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Batch delete assessments
     */
    async batchDeleteAssessments(ids) {
        const store = useAssessmentStore.getState();
        if (store.setLoading) store.setLoading(true);
        try {
            const response = await this.service.bulkDelete(ids);
            toast.success(i18next.t('internalAuditAssessment:messages.bulkDeleteSuccess', { defaultValue: 'Selected items deleted successfully' }));
            this.fetchAssessments();
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    async fetchCalendarData() {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!tenantId) return;

        store.setCalendarLoading(true);
        store.setCalendarError(null);

        try {
            const [assessRes, docsRes] = await Promise.all([
                this.service.getAll({ page: 1, pageSize: 1000, search: "", tenantId }),
                this.questionService.getAll({ page: 1, pageSize: 1000, search: "", tenantId }),
            ]);

            store.setCalendarData({
                assessments: Array.isArray(assessRes?.data) ? assessRes.data : [],
                questions: Array.isArray(docsRes?.data) ? docsRes.data : [],
            });
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('internalAuditAssessment:messages.calendarLoadFailed', {
                defaultValue: 'Could not load calendar data',
            });
            store.setCalendarError(msg);
            toast.error(msg);
        } finally {
            store.setCalendarLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
